import { useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';
import { CheckboxField } from '~/components/checkbox';
import { Box } from '~/components/layout';
import { SelectField } from '~/components/select';
import { TextInputField } from '~/components/text-input';
import { Text } from '~/components/typography';

import { Values } from '../../hooks/use-form';
import { DoubleTheDonation } from '../double-the-donation';
import { FormSection } from '../form-section';
import IconPerson from './assets/icon-person.svg';
import { UNITED_STATES, useCountryOptions } from './hooks/use-country-options';
import { useStateOptions } from './hooks/use-state-options';

import type { PageFeatures, PageNonprofit, PublicAllocation } from '~/types/graphql';
import { WORKSPACE_IDS } from '~/config/workspaces';
import { CustomFieldsSection } from '~/components/custom-fields/CustomFieldsSection';
import { PageCustomField } from '~/utils/custom-field-utils'

type Props = {
  allocations?: PublicAllocation[];
  nonprofit: PageNonprofit;
  features: PageFeatures;
  smsMarketingEnabled?: boolean;
  isEvent?: boolean;
  heading?: string;
  customFields?: PageCustomField[]
  className?: string
};

export const DonorInformation = ({
  nonprofit,
  allocations,
  features,
  smsMarketingEnabled = false,
  isEvent,
  heading: headingOverride,
  customFields = [],
  className
}: Props) => {
  const { values, setFieldValue } = useFormikContext<Values>();
  const { allocations: showAllocations, addressForm: showAddress, showCompanyForm } = features;

  const allocationOptions = useMemo(() => {
    if (!allocations) {
      return [{ value: '', label: 'Where it is needed most' }];
    }

    return [{ value: '', label: 'Where it is needed most' }].concat(
      allocations.map(allocation => ({
        label: allocation.name,
        value: allocation.id
      }))
    );
  }, [allocations]);

  const countryOptions = useCountryOptions();
  const stateOptions = useStateOptions();
  const isUnitedStates = values.address.country === UNITED_STATES;

  // Clear the state field when switching away from the US, where it isn't collected,
  // so a previously selected US state isn't saved on a non-US address.
  useEffect(() => {
    if (!isUnitedStates && values.address.state) {
      setFieldValue('address.state', '');
    }
  }, [isUnitedStates, values.address.state, setFieldValue]);

  return (
    <FormSection icon={<IconPerson />} heading={headingOverride || 'Donor Information'} className={className}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <TextInputField label="First Name" name="firstName" autoComplete="given-name" />
        </div>
        <div>
          <TextInputField label="Last Name" name="lastName" autoComplete="family-name" />
        </div>
        <div className="md:col-span-2">
          <TextInputField label="Email Address" name="email" autoComplete="email" />
        </div>
        {showCompanyForm && (
          <div className="md:col-span-2">
            <TextInputField
              label="Company"
              name="companyName"
              autoComplete="organization"
              optional
            />
          </div>
        )}
        {showAddress ? (
          <>
            <TextInputField
              label="Street Address"
              name="address.addressLine1"
              autoComplete="billing address-line1"
            />
            <TextInputField
              label="Apartment, suite, etc."
              name="address.addressLine2"
              autoComplete="billing address-line2"
              optional
            />
            <TextInputField
              label="City"
              name="address.city"
              autoComplete="billing address-level2"
            />
            <SelectField
              label="Country"
              name="address.country"
              options={countryOptions}
              autoComplete="billing country-name"
            />
            <TextInputField
              label={isUnitedStates ? 'ZIP' : 'Postal Code'}
              name="address.zip"
              autoComplete="billing postal-code"
            />
            {isUnitedStates && (
              <SelectField
                label="State"
                name="address.state"
                options={stateOptions}
                placeholder="Select a State"
                autoComplete="billing address-level1"
              />
            )}
          </>
        ) : (
          <></>
        )}

        {nonprofit.webId !== WORKSPACE_IDS.THE_ADVOCATE && (
          <div className="md:col-span-2">
            <TextInputField
              label="Phone"
              name="address.phone"
              optional
              autoComplete="tel"
              gridColumn={[null, '1 / 3']}
            />
            {smsMarketingEnabled && (
              <div className="text-xs text-gray-500 mt-2 leading-[1rem]">
                By providing your phone number, you agree to receive fundraising SMS messages from{' '}
                {nonprofit.name} no more than once a day. Message & data rates may apply. Reply STOP
                to unsubscribe.
              </div>
            )}
          </div>
        )}

        {nonprofit.doubleTheDonationPublicKey && (
          <Box gridColumn={[null, '1 / 3']}>
            <DoubleTheDonation publicKey={nonprofit.doubleTheDonationPublicKey ?? ''} />
          </Box>
        )}
        {showAllocations ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Text bold marginBottom="0.25rem">
              Apply Your Gift to
            </Text>
            <Box marginBottom="1.5rem">
              <SelectField
                label=""
                name="allocationId"
                options={allocationOptions}
                placeholder="Where it is needed most"
                gridColumn={[null, '1 / 3']}
              />
            </Box>
          </div>
        ) : (
          <></>
        )}
        {!isEvent && (
          <div className="grid grid-cols-1 gap-4 md:col-span-2">
            <CheckboxField name="anonymous" label="Make my donation anonymous" />
            <CheckboxField
              name="showDedication"
              label="Dedicate my donation in honor or in memory of someone"
            />
          </div>
        )}

        <CustomFieldsSection fields={customFields} />
      </div>
    </FormSection>
  );
};
